import type { FormField, InterviewData } from '../types';
import { getResponseDisplayStatus, getResponseEmail, responseStatusConfig } from './admin-utils';
import { getStructuredResponseAnswers } from './response-completion';

import type { CampaignExportFilters } from './campaign-export-filters';
export type { CampaignExportFilters } from './campaign-export-filters';

/** The visible response projection fetched for CSV exports. */
export type CampaignExportResponse = Pick<InterviewData,
  'id' | 'response_number' | 'response_status' | 'created_at' | 'completed_at'
  | 'interviewee_name' | 'interviewee_email' | 'interviewee_company' | 'interviewee_job'
  | 'calendar_bookings' | 'form_answers' | 'form_schema_snapshot' | 'conversation_turns'>;

/** Convert inclusive calendar dates to an exclusive upper timestamp bound. */
export function getExportDateBounds(filters: CampaignExportFilters): { from?: string; until?: string } {
  if (filters.timezone) new Intl.DateTimeFormat('en', { timeZone: filters.timezone });
  const start = filters.startDate ? parseLocalDate(filters.startDate) : undefined;
  const end = filters.endDate ? parseLocalDate(filters.endDate) : undefined;
  if (start && end && start > end) throw new Error('End date must be on or after start date.');
  if (end) {
    end.setDate(end.getDate() + 1);
    // Re-anchor the next day at its own start: a skipped end-date midnight parses as 01:00.
    end.setHours(0, 0, 0, 0);
  }
  return {
    ...(start ? { from: zonedMidnight(start, filters.timezone) } : {}),
    ...(end ? { until: zonedMidnight(end, filters.timezone) } : {}),
  };
}

/** One calendar day in milliseconds; every IANA offset is well within it. */
const DAY_MS = 86_400_000;

/**
 * Resolve the first instant of a calendar day in an IANA timezone, preserving
 * DST date boundaries. That is local midnight, or the DST transition when the
 * clocks skip midnight (for example America/Santiago jumps from 23:59 to 01:00).
 */
function zonedMidnight(date: Date, timezone?: string): string {
  if (!timezone) return date.toISOString();
  const target = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const formatter = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  /** The instant's wall-clock time in the timezone, expressed as a UTC timestamp. */
  const wallClock = (instant: number): number => {
    const parts = Object.fromEntries(formatter.formatToParts(instant).map(part => [part.type, part.value]));
    return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second));
  };
  let instant = target;
  for (let attempt = 0; attempt < 4; attempt++) {
    const adjustment = target - wallClock(instant);
    if (!adjustment) return new Date(instant).toISOString();
    instant += adjustment;
  }
  // No exact local midnight: the day starts at the first instant its wall clock reaches.
  let before = target - DAY_MS;
  let after = target + DAY_MS;
  while (after - before > 1) {
    const middle = Math.floor((before + after) / 2);
    if (wallClock(middle) < target) before = middle;
    else after = middle;
  }
  return new Date(after).toISOString();
}

/** Parse calendar dates without UTC coercion or silently rolling an invalid day forward. */
function parseLocalDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error('Choose a valid date.');
  const [, year, month, day] = match.map(Number);
  const date = new Date(0);
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error('Choose a valid date.');
  }
  return date;
}

/** Quote every cell and prevent untrusted text from executing as a spreadsheet formula. */
function csvCell(value: string | number | null | undefined): string {
  const text = value == null ? '' : String(value);
  const safe = typeof value === 'string' && (/^\s*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text)) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Fixed metadata headings also participate in question-label collision checks. */
const METADATA_COLUMNS = ['Response ID', 'Response number', 'Name', 'Email', 'Company', 'Job title', 'Status', 'Created at (UTC)', 'Completed at (UTC)'];

/** Build spreadsheet columns from the campaign and response snapshots. */
export function buildCampaignCsv(fields: FormField[], responses: CampaignExportResponse[]): string {
  const ordered = [...responses].sort((a, b) => (a.response_number ?? 0) - (b.response_number ?? 0) || a.id.localeCompare(b.id));
  const questions = new Map<string, FormField>();
  for (const schema of [fields, ...ordered.map(response => response.form_schema_snapshot ?? [])]) {
    for (const field of schema) {
      if (field.type !== 'information' && !questions.has(field.id)) questions.set(field.id, field);
    }
  }
  const columns = [...questions.values()];
  const labelCounts = new Map<string, number>();
  for (const label of [...METADATA_COLUMNS, ...columns.map(field => field.label), 'Transcript']) {
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
  }
  const usedLabels = new Set([...METADATA_COLUMNS, 'Transcript']);
  const headers = columns.map(field => {
    const base = (labelCounts.get(field.label) ?? 0) > 1 ? `${field.label} [${field.id}]` : field.label;
    let label = base;
    let suffix = 2;
    while (usedLabels.has(label)) label = `${base} (${suffix++})`;
    usedLabels.add(label);
    return label;
  });
  const rows: Array<Array<string | number | undefined>> = [[...METADATA_COLUMNS, ...headers, 'Transcript']];
  for (const response of ordered) {
    // Pre-snapshot responses used campaign field names. An empty snapshot is authoritative.
    const schema = response.form_schema_snapshot ?? fields;
    const answers = new Map<string, string | number>(getStructuredResponseAnswers({ ...response, form_schema_snapshot: schema })
      .map(answer => [answer.fieldId, typeof answer.value === 'number' ? answer.value : answer.displayValue]));
    for (const field of schema) {
      if (field.type === 'file_upload') {
        const ids = response.form_answers?.[field.name];
        if (Array.isArray(ids) && ids.every(id => typeof id === 'string')) answers.set(field.id, ids.join(', '));
      } else if (field.type === 'calendar_booking') {
        const bookings = (response.calendar_bookings ?? []).filter(booking => booking.fieldName === field.name);
        answers.set(field.id, bookings.map(booking => `${booking.startIso} to ${booking.endIso}`).join('\n'));
      }
    }
    const status = getResponseDisplayStatus(response);
    const transcript = (response.conversation_turns ?? [])
      .filter(turn => turn.speaker === 'ai' || turn.speaker === 'user')
      .map(turn => `${turn.speaker === 'ai' ? 'Host' : 'Guest'}: ${turn.text}`).join('\n\n');
    rows.push([
      response.id, response.response_number, response.interviewee_name, getResponseEmail(response),
      response.interviewee_company, response.interviewee_job, responseStatusConfig[status].label,
      response.created_at, response.completed_at,
      ...columns.map(field => answers.get(field.id) ?? ''), transcript,
    ]);
  }
  // BOM and CRLF make Excel recognize UTF-8 and multiline records consistently.
  return `\uFEFF${rows.map(row => row.map(csvCell).join(',')).join('\r\n')}\r\n`;
}
